import { ArrowRight, Play, Save, GitBranch, Bot, CheckCircle, Zap, PlusCircle, Calendar } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

/**
 * Workflow Canvas Mockup component that visually represents the actual workflow builder interface.
 * This is a non-functional visual representation used for marketing purposes.
 */
function WorkflowCanvasMockup() {
  return (
    <div className="w-full rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded bg-neutral-800 flex items-center justify-center">
            <ArrowRight className="h-3 w-3 rotate-180 text-neutral-500" />
          </div>
          <span className="text-sm font-medium text-white">Customer Support Flow</span>
          <span className="rounded bg-primary/20 px-2 py-0.5 text-xs text-primary">Active</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded bg-neutral-800 px-2.5 py-1 text-xs text-neutral-400 flex items-center gap-1">
            <Play className="h-3 w-3" />
            Run
          </div>
          <div className="rounded bg-primary px-2.5 py-1 text-xs text-white flex items-center gap-1">
            <Save className="h-3 w-3" />
            Save
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex min-h-[320px]">
        {/* Left: Mini Node Palette */}
        <div className="w-48 border-r border-neutral-800 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-3">
            Nodes
          </div>
          <div className="space-y-2">
            {[
              { icon: <Zap className="h-3 w-3" />, label: "Trigger", color: "bg-yellow-500/20 text-yellow-400" },
              { icon: <Bot className="h-3 w-3" />, label: "AI Agent", color: "bg-purple-500/20 text-purple-400" },
              { icon: <GitBranch className="h-3 w-3" />, label: "Conditional", color: "bg-blue-500/20 text-blue-400" },
              { icon: <CheckCircle className="h-3 w-3" />, label: "Action", color: "bg-green-500/20 text-green-400" },
            ].map((node, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 cursor-grab"
              >
                <div className={`rounded p-1 ${node.color}`}>
                  {node.icon}
                </div>
                <span className="text-xs text-neutral-300">{node.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Canvas with Nodes */}
        <div className="flex-1 relative p-6 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:20px_20px]">
          {/* Start Node */}
          <div className="absolute top-8 left-8">
            <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-neutral-800 px-3 py-2 shadow-lg">
              <div className="rounded bg-yellow-500/20 p-1">
                <Zap className="h-4 w-4 text-yellow-400" />
              </div>
              <div>
                <div className="text-xs font-medium text-white">Webhook</div>
                <div className="text-xs text-neutral-500">On request</div>
              </div>
            </div>
          </div>

          {/* Connection Line 1 */}
          <svg className="absolute top-[68px] left-[160px] w-[60px] h-[80px]" style={{ overflow: 'visible' }}>
            <path
              d="M0,0 C30,0 30,80 60,80"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
          </svg>

          {/* AI Agent Node */}
          <div className="absolute top-[120px] left-[220px]">
            <div className="flex items-center gap-2 rounded-lg border border-purple-500/30 bg-neutral-800 px-3 py-2 shadow-lg">
              <div className="rounded bg-purple-500/20 p-1">
                <Bot className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <div className="text-xs font-medium text-white">Classify Intent</div>
                <div className="text-xs text-neutral-500">GPT-4o</div>
              </div>
            </div>
          </div>

          {/* Connection Line 2 */}
          <svg className="absolute top-[165px] left-[360px] w-[40px] h-[30px]" style={{ overflow: 'visible' }}>
            <path
              d="M0,0 C20,0 20,30 40,30"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
          </svg>

          {/* Conditional Node */}
          <div className="absolute top-[170px] left-[400px]">
            <div className="flex items-center gap-2 rounded-lg border border-blue-500/30 bg-neutral-800 px-3 py-2 shadow-lg">
              <div className="rounded bg-blue-500/20 p-1">
                <GitBranch className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <div className="text-xs font-medium text-white">Route</div>
                <div className="text-xs text-neutral-500">By intent</div>
              </div>
            </div>
          </div>

          {/* Branch 1: Connection & End Node */}
          <svg className="absolute top-[215px] left-[440px] w-[40px] h-[45px]" style={{ overflow: 'visible' }}>
            <path
              d="M0,0 C0,22 40,22 40,45"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
          </svg>

          <div className="absolute top-[260px] left-[460px]">
            <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-neutral-800 px-3 py-2 shadow-lg">
              <div className="rounded bg-green-500/20 p-1">
                <CheckCircle className="h-4 w-4 text-green-400" />
              </div>
              <div>
                <div className="text-xs font-medium text-white">Reply</div>
                <div className="text-xs text-neutral-500">Send email</div>
              </div>
            </div>
          </div>

          {/* Add Node Button */}
          <div className="absolute bottom-8 right-8">
            <div className="flex items-center justify-center h-8 w-8 rounded-full border-2 border-dashed border-neutral-600 bg-neutral-800/50 cursor-pointer hover:border-primary/50 transition-colors">
              <PlusCircle className="h-4 w-4 text-neutral-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Hero section for the Workflow Builder product page.
 * Features a dark gradient background with a mockup of the workflow canvas.
 */
export function HeroSection() {
  const navigate = useNavigate()

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Text Content - Centered */}
        <div className="mb-12 text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            Workflow Builder
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Automate Any Process with{" "}
            <span className="text-primary">Visual Workflows</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-400">
            Build powerful automations by connecting nodes visually. From simple
            sequences to complex multi-branch processes - no coding required.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="group bg-primary text-white hover:bg-primary/90"
              onClick={() => navigate("/signup")}
            >
              Start Automating
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              onClick={() => navigate("/demo")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo
            </Button>
          </div>
        </div>

        {/* Workflow Canvas Mockup */}
        <div className="relative">
          <WorkflowCanvasMockup />
          {/* Decorative glow */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 opacity-50 blur-2xl" />
        </div>
      </div>
    </section>
  )
}
